import { useEffect, useMemo, useRef, useState } from "react";
import { commonSectionOptions, commonSectionPresentation, sectionPresentation, stableAlignmentKey } from "../lib/legalSections";
import { hasDisplayText } from "../lib/displayText";
import { formatTimelineDate } from "../lib/legalTimeline";
import { typeLabel } from "../lib/legalTaxonomy";
import { useLocale } from "../lib/LocaleProvider";
import { resolveDocumentTitle } from "../lib/documentTitle";
import LegalText from "./LegalText";

function focusedSegment(item) {
  const segments = item.bundle?.segments || [];
  const target = segments.find((candidate) => Number(candidate.sequence) === Number(item.targetSequence));
  return (hasDisplayText(target) && target) || segments.find(hasDisplayText) || null;
}

function ComparisonDocument({ item, index, onRemove, sharedSectionKey, onSharedSectionChange, sharedSectionKeys, readingMode, active, onCitationOpen, onSourceOpen }) {
  const { locale, text } = useLocale();
  const initial = focusedSegment(item);
  const [sequence, setSequence] = useState(initial?.sequence ?? "");
  const fullTextRef = useRef(null);
  const syncingRef = useRef(false);
  useEffect(() => setSequence(focusedSegment(item)?.sequence ?? ""), [item]);
  const selectable = (item.bundle?.segments || []).filter(hasDisplayText);
  const sharedSegment = sharedSectionKey ? selectable.find((candidate) => stableAlignmentKey(candidate) === sharedSectionKey) : null;
  const segment = sharedSegment || selectable.find((candidate) => Number(candidate.sequence) === Number(sequence)) || initial;
  const title = resolveDocumentTitle(item.bundle.document, locale);
  const segmentHeading = sectionPresentation(segment, {
    locale,
    language: item.bundle.expression.language,
  });

  useEffect(() => {
    if (readingMode !== "full" || !sharedSectionKey || !active) return;
    const container = fullTextRef.current;
    const target = container?.querySelector(`[data-section-key="${CSS.escape(sharedSectionKey)}"]`);
    if (!container || !target) return;
    const top = target.offsetTop - container.offsetTop;
    if (Math.abs(container.scrollTop - top) < 12) return;
    syncingRef.current = true;
    container.scrollTo({ top, behavior: "auto" });
    window.setTimeout(() => { syncingRef.current = false; }, 80);
  }, [active, readingMode, sharedSectionKey]);

  function syncFromScroll(event) {
    if (!sharedSectionKeys.length || syncingRef.current) return;
    const container = event.currentTarget;
    const anchors = [...container.querySelectorAll("[data-section-key]")];
    const current = anchors.reduce((best, candidate) => {
      const distance = Math.abs(candidate.offsetTop - container.scrollTop - container.offsetTop);
      return !best || distance < best.distance ? { candidate, distance } : best;
    }, null)?.candidate;
    const key = current?.dataset.sectionKey;
    if (key && sharedSectionKeys.includes(key) && key !== sharedSectionKey) onSharedSectionChange(key);
  }

  return (
    <article className="compare-document">
      <header>
        <span className="compare-label">{String.fromCharCode(65 + index)}</span>
        <div>
          <p>{typeLabel(item.bundle.document.document_type_code, locale)}</p>
          <h2 lang={title.language}>{title.text}</h2>
          {title.isFallback && title.text && <small lang={locale}>{text(title.language === "th" ? "reader.titleInThai" : "reader.titleInEnglish")}</small>}
          <small>{[item.bundle.document.official_number, formatTimelineDate(item.bundle.document.issued_date || item.bundle.document.signed_date, locale)].filter(Boolean).join(" · ")}</small>
        </div>
        <button type="button" onClick={() => onRemove(item.expressionId)}>{text("comparison.remove")}</button>
      </header>
      {readingMode === "section" && selectable.length > 1 && (
        <label className="compare-section-select">
          <span>{text("comparison.sectionShown")}</span>
          <select value={segment?.sequence ?? sequence} onChange={(event) => {
            const nextSequence = event.target.value;
            const nextSegment = selectable.find((candidate) => Number(candidate.sequence) === Number(nextSequence));
            setSequence(nextSequence);
            const key = stableAlignmentKey(nextSegment);
            if (key && sharedSectionKeys.includes(key)) onSharedSectionChange(key);
          }}>
            {selectable.map((candidate) => {
              const label = sectionPresentation(candidate, { locale, language: item.bundle.expression.language });
              return <option value={candidate.sequence} key={candidate.sequence} lang={label.language}>{label.text}</option>;
            })}
          </select>
        </label>
      )}
      {readingMode === "full" ? (
        <div className="compare-full-text" ref={fullTextRef} onScroll={syncFromScroll}>
          {selectable.map((candidate) => {
            const key = stableAlignmentKey(candidate);
            return (
              <section className={key && key === sharedSectionKey ? "synced-section" : ""} data-section-key={key || undefined} key={candidate.sequence}>
                <strong lang={sectionPresentation(candidate, { locale, language: item.bundle.expression.language }).language}>
                  {sectionPresentation(candidate, { locale, language: item.bundle.expression.language }).text}
                </strong>
                <LegalText
                  segment={candidate}
                  citations={item.bundle.citations}
                  language={item.bundle.expression.language}
                  onCitationOpen={onCitationOpen}
                  onSourceOpen={onSourceOpen}
                  source={item.bundle.source_item}
                />
              </section>
            );
          })}
        </div>
      ) : (
        <div className="compare-legal-text">
          <strong lang={segmentHeading.language}>{segmentHeading.text}</strong>
          {segment ? (
            <LegalText
              segment={segment}
              citations={item.bundle.citations}
              language={item.bundle.expression.language}
              onCitationOpen={onCitationOpen}
              onSourceOpen={onSourceOpen}
              source={item.bundle.source_item}
            />
          ) : (
            <p>{text("comparison.noSection")}</p>
          )}
        </div>
      )}
    </article>
  );
}

function EmptyComparisonSlot({ index }) {
  const { text } = useLocale();
  return (
    <article className="compare-document comparison-empty-slot">
      <header><span className="compare-label muted">{String.fromCharCode(65 + index)}</span><div><p>{text("comparison.emptyLabel")}</p><h2>{text("comparison.emptyTitle")}</h2></div></header>
      <p>{text("comparison.emptyHint")}</p>
    </article>
  );
}

function ComparisonPicker({ availableDocuments = [], items, busy, onSearch, onAddDocument }) {
  const { text } = useLocale();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const existing = new Set(items.map((item) => item.expressionId));
  const candidates = availableDocuments.filter((item) => {
    if (existing.has(item.expression_id)) return false;
    if (!query.trim()) return true;
    const haystack = [item.title, item.exact_title, item.official_number, item.document_type]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();
    return haystack.includes(query.trim().toLocaleLowerCase());
  });

  return (
    <form
      className="comparison-picker"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch?.(query);
      }}
    >
      <label>
        <span>{text("comparison.search")}</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={text("comparison.searchPlaceholder")}
          type="search"
          autoComplete="off"
        />
      </label>
      <button type="submit" disabled={busy || !query.trim()}>{text("comparison.searchSubmit")}</button>
      <label>
        <span>{text("comparison.chooseDocument")}</span>
        <select
          value={selectedId}
          onChange={async (event) => {
            const nextId = event.target.value;
            setSelectedId(nextId);
            if (!nextId) return;
            const candidate = candidates.find((item) => item.expression_id === nextId);
            if (candidate) await onAddDocument?.(candidate);
            setSelectedId("");
          }}
          disabled={!candidates.length || busy}
        >
          <option value="">{candidates.length ? text("comparison.chooseDocument") : text("comparison.noCandidates")}</option>
          {candidates.map((candidate) => (
            <option value={candidate.expression_id} key={candidate.expression_id}>
              {candidate.title || candidate.exact_title || candidate.official_number || candidate.expression_id}
            </option>
          ))}
        </select>
      </label>
    </form>
  );
}

export default function ComparisonDrawer({
  open,
  items,
  availableDocuments,
  busy,
  onSearch,
  onAddDocument,
  onClose,
  onRemove,
  onClear,
  onCitationOpen,
  onSourceOpen,
}) {
  const { locale, text, count } = useLocale();
  const [mobileIndex, setMobileIndex] = useState(0);
  const [readingMode, setReadingMode] = useState("full");
  const sharedSections = useMemo(() => commonSectionOptions(items, locale), [items, locale]);
  const sharedSectionKeys = useMemo(() => sharedSections.map((option) => option.key), [sharedSections]);
  const sectionCoverage = useMemo(() => {
    const union = new Set(items.flatMap((item) => (item.bundle?.segments || []).map(stableAlignmentKey).filter(Boolean)));
    return { matched: sharedSections.length, total: union.size };
  }, [items, sharedSections]);
  const [sharedSection, setSharedSection] = useState("");
  useEffect(() => setMobileIndex((current) => Math.min(current, Math.max(items.length - 1, 0))), [items.length]);
  useEffect(() => {
    if (!sharedSections.length) {
      setSharedSection("");
      return;
    }
    setSharedSection((current) => sharedSections.some((option) => option.key === current) ? current : sharedSections[0].key);
  }, [sharedSections]);
  if (!open) return null;
  if (!items.length) {
    return (
      <section className="comparison-workspace" aria-label={text("comparison.aria")}>
        <header className="comparison-header">
          <div><h1>{text("comparison.label")}</h1></div>
          <button type="button" className="comparison-close" onClick={onClose}>{text("comparison.back")}</button>
        </header>
        <ComparisonPicker
          availableDocuments={availableDocuments}
          items={items}
          busy={busy}
          onSearch={onSearch}
          onAddDocument={onAddDocument}
        />
        <div className="empty-state comparison-empty-state"><p>{text("comparison.emptyTitle")}</p><span>{text("comparison.emptyHint")}</span></div>
      </section>
    );
  }
  const desktopItems = [...items];
  while (desktopItems.length < 2) desktopItems.push(null);
  return (
    <section className="comparison-workspace" aria-label={text("comparison.aria")}>
      <header className="comparison-header">
        <div><h1>{text("comparison.label")}</h1></div>
        <div className="comparison-actions">
          <span>{text("comparison.count", { count: count(items.length) })}</span>
          {items.length > 0 && <button type="button" onClick={onClear}>{text("comparison.clear")}</button>}
          <button type="button" className="comparison-close" onClick={onClose}>{text("comparison.back")}</button>
        </div>
      </header>
      <ComparisonPicker
        availableDocuments={availableDocuments}
        items={items}
        busy={busy}
        onSearch={onSearch}
        onAddDocument={onAddDocument}
      />
      <details className="comparison-timeline-disclosure">
        <summary>{text("comparison.timeline")}</summary>
        <div className="comparison-timeline" aria-label={text("comparison.timeline")}>
          {desktopItems.map((item, index) => {
            const date = item && formatTimelineDate(item.bundle.document.issued_date || item.bundle.document.signed_date, locale);
            return (
              <div className={item ? "active" : ""} key={item?.expressionId || `slot-${index}`}>
                <span>{String.fromCharCode(65 + index)}</span>
                {date && <p>{date}</p>}
              </div>
            );
          })}
        </div>
      </details>
      <div className="comparison-reading-mode" aria-label={text("comparison.label")}>
        <button type="button" className={readingMode === "full" ? "active" : ""} onClick={() => setReadingMode("full")}>
          {text("comparison.full")}<small>{text("comparison.fullHint")}</small>
        </button>
        <button type="button" className={readingMode === "section" ? "active" : ""} onClick={() => setReadingMode("section")}>
          {text("comparison.section")}<small>{text("comparison.sectionHint")}</small>
        </button>
      </div>
      {sharedSections.length > 0 && (
        <label className="comparison-shared-section">
          <span>{text("comparison.sharedSection")}</span>
          <select value={sharedSection} onChange={(event) => setSharedSection(event.target.value)}>
            {sharedSections.map((option) => {
              const presentation = commonSectionPresentation(items, option.key, locale);
              return <option value={option.key} key={option.key} lang={presentation.language}>{option.label}</option>;
            })}
          </select>
        </label>
      )}
      <p className={`comparison-alignment-status ${sharedSections.length ? "aligned" : "unaligned"}`}>
        {sharedSections.length
          ? text("comparison.alignment", { matched: count(sectionCoverage.matched), total: count(sectionCoverage.total) })
          : text("comparison.noAlignment")}
      </p>
      <div className="comparison-mobile-toggle" aria-label={text("comparison.timeline")}>
        {desktopItems.map((item, index) => <button type="button" disabled={!item} className={mobileIndex === index ? "active" : ""} onClick={() => setMobileIndex(index)} key={`mobile-${index}`}>{String.fromCharCode(65 + index)}</button>)}
      </div>
      <div className="comparison-grid">
        {desktopItems.map((item, index) => (
          <div className={mobileIndex === index ? "compare-column active" : "compare-column"} key={item?.expressionId || `column-${index}`}>
            {item ? <ComparisonDocument item={item} index={index} onRemove={onRemove} sharedSectionKey={sharedSection} onSharedSectionChange={setSharedSection} sharedSectionKeys={sharedSectionKeys} readingMode={readingMode} active={mobileIndex === index || (typeof window !== "undefined" && window.innerWidth > 800)} onCitationOpen={onCitationOpen} onSourceOpen={onSourceOpen} /> : <EmptyComparisonSlot index={index} />}
          </div>
        ))}
      </div>
    </section>
  );
}
